import { ChangeData } from "../interfaces/change-data";
import { FrontError } from "../interfaces/front-error";
import { LoginInfo } from "../interfaces/login-info";
import { UserInfo } from "../interfaces/user-info";
import { TableData } from "../types/table-filter-types";
import { checkReponse, request } from "../utils/commonApi";
import { SubmitDataParams } from "./reducers/upload-file";
import { EmassarMethod } from "../types/emassar-method";
import { CutAndFill, FeatureInfo } from "../interfaces/feature-info";
import { GetCutAndFillOptions } from "../interfaces/cutAndFill";
import {
  KrookieDataResponse,
  ResponseCoordinates,
} from "../interfaces/krookie";
import {
  ReportHistoryResponse,
  ResponseReports,
} from "./reducers/reports-journal";
import {
  GetProjectLogsResponse,
  getProjectsResponse,
  ProjectsResponse,
} from "../interfaces/utilities";
import { Layer } from "../interfaces/layer";
import { ArcgisTokenMap, GetFields } from "../utils/feature-service";

export const BASE_URL = process.env.REACT_APP_API_URL;
export const CONVERTER_URL = process.env.REACT_APP_CONVERTER_API_URL;
export const KROOKIE_URL = process.env.REACT_APP_KROOKIE_URL;
export const REPORTS_URL = process.env.REACT_APP_REPORTS_URL;
export const UTILITIES_URL = process.env.REACT_APP_UTILITIES_URL;

export const login = async (loginInfo: LoginInfo) => {
  try {
    const response = await request(`${BASE_URL}/login`, {
      method: "POST",
      body: JSON.stringify(loginInfo),
    });
    localStorage.setItem("accessToken", response.accessToken);
    localStorage.setItem("refreshToken", response.refreshToken);
    return response;
  } catch (err) {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");

    if (typeof err === "object" && err !== null) {
      const errMessage = (err as FrontError).message || "Unknown login error";
      throw new Error(errMessage);
    }
  }
};
export const logout = async () => {
  await request(`${BASE_URL}/logout`, {
    method: "POST",
    body: JSON.stringify({
      token: localStorage.getItem("refreshToken"),
    }),
  });
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
};
export const createUser = async (userInfo: UserInfo) => {
  try {
    const response = await fetchWithRefresh(`${BASE_URL}/createUser`, {
      method: "POST",
      body: JSON.stringify(userInfo),
      headers: {
        Authorization: localStorage.getItem("accessToken")!,
      },
    });
    return response;
  } catch (e) {
    console.log(e);
  }
};
export const getUser = async () => {
  try {
    const response = await fetchWithRefresh(`${BASE_URL}/getUser`, {
      headers: {
        Authorization: localStorage.getItem("accessToken")!,
      },
    });
    return response;
  } catch (e) {
    return Promise.reject(e);
  }
};
export const getUsers = async () => {
  try {
    const response = await fetchWithRefresh(`${BASE_URL}/getUsers`, {
      headers: {
        Authorization: localStorage.getItem("accessToken")!,
      },
    });
    return response;
  } catch (e) {
    return Promise.reject(e);
  }
};
export const getLayers = async () => {
  try {
    const token = localStorage.getItem("accessToken");
    const headers: HeadersInit = {};
    if (token) headers.Authorization = token;

    const url = `${BASE_URL}/getLayers`;
    if (token) {
      return await fetchWithRefresh(url, { headers });
    }
    return await request(url);
  } catch (e) {
    return Promise.reject(e);
  }
};
export const updateArcgisToken = async (host: string) => {
  try {
    const accessToken = localStorage.getItem("accessToken");
    const headers: HeadersInit = {};
    if (accessToken) headers.Authorization = accessToken;

    const url = `${BASE_URL}/getArcgisToken?host=${encodeURIComponent(host)}`;
    if (accessToken) {
      return await fetchWithRefresh(url, { headers });
    }
    return await request(url, { headers });
  } catch (e) {
    return Promise.reject(e);
  }
};

export const sendFeaturesChange = async (changeData: ChangeData) => {
  try {
    const response = await fetchWithRefresh(`${BASE_URL}/featuresChange`, {
      method: "POST",
      headers: {
        Authorization: localStorage.getItem("accessToken")!,
        "Content-Type": "application/json;charset=utf-8",
      },
      body: JSON.stringify(changeData),
    });
    return response;
  } catch (e) {
    console.log(e);
  }
};
export const fetchWithRefresh = async (
  url: string,
  options: RequestInit,
  returnRawResponse = false,
) => {
  try {
    const res = await fetch(url, options);
    if (returnRawResponse) return res;
    return await checkReponse(res);
  } catch (e) {
    const err = e as { message: string };
    if (err.message === "jwt expired") {
      const refreshData = await refreshToken();
      options.headers = {
        ...options.headers,
        Authorization: refreshData.accessToken,
      };
      const res = await fetch(url, options);
      return await checkReponse(res);
    } else {
      return Promise.reject(e);
    }
  }
};

const handleAuthFailure = () => {
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("accessToken");
  window.location.href = `${process.env.PUBLIC_URL}/login`;
};

export const refreshToken = () => {
  const token = localStorage.getItem("refreshToken");

  if (!token) {
    handleAuthFailure();
    return Promise.reject(new Error("No refresh token found"));
  }

  return fetch(`${BASE_URL}/refreshToken`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json;charset=utf-8",
    },
    body: JSON.stringify({ token }),
  })
    .then(checkReponse)
    .then((refreshData) => {
      localStorage.setItem("refreshToken", refreshData.refreshToken);
      localStorage.setItem("accessToken", refreshData.accessToken);
      return refreshData;
    })
    .catch((err) => {
      handleAuthFailure();
      return Promise.reject(err);
    });
};

export const sendError = (url: string, error: FrontError) => {
  try {
    const response = request(`${url}/sendError`, {
      method: "POST",
      body: JSON.stringify(error),
    });
    return response;
  } catch (e) {
    console.log(e);
  }
};

export const submitDataApi = async ({
  layer,
  files,
  coordinatesSystem,
}: SubmitDataParams) => {
  const formData = new FormData();

  formData.append("LayerId", layer.id.toString());

  if (files.shp && files.shx && files.dbf) {
    formData.append("Files", files.shp);
    formData.append("Files", files.shx);
    formData.append("Files", files.dbf);
  } else if (files.dxf) {
    formData.append("Files", files.dxf);
  }

  formData.append("SrId", coordinatesSystem || "");

  try {
    const response = await fetch(`${CONVERTER_URL}/Convert`, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Server responded with an error: ${errorText}`);
    }

    return await response.json();
  } catch (error: any) {
    throw new Error(error.message || "Unknown error occurred");
  }
};
export const getTerrains = async () => {
  try {
    return await request(`${BASE_URL}/getTerrains`);
  } catch (e) {
    return Promise.reject(e);
  }
};

// TABLE TEST

interface FakeTableData {
  data: TableData;
  page: number;
  pageSize: number;
  totalDataSize: number;
}

// TABLE TEST

export const getEmassarData = async (
  methodName: EmassarMethod,
  idsArr: number[],
) => {
  let result;

  await fetch(BASE_URL + `/GetEmassarData?methodName=${methodName}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(idsArr),
  })
    .then((response) => {
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      return response.json();
    })
    .then((data) => {
      result = data;
    })
    .catch((error) => {
      console.error("Error:", error);
    });

  return result;
};

export const getFeaturesInfo = async (layerId: number) => {
  let result;

  await fetch(BASE_URL + `/GetFeaturesInfo?layerId=${layerId}`)
    .then((response) => {
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      return response.json();
    })
    .then((data) => {
      result = data;
    })
    .catch((error) => {
      console.error("Error:", error);
    });

  return result;
};

export const getFeatureInfo = async (
  layerId: number,
  featureId: number,
): Promise<FeatureInfo | undefined> => {
  let result: FeatureInfo | undefined;

  await fetch(
    BASE_URL + `/GetFeatureInfo?layerId=${layerId}&featureId=${featureId}`,
  )
    .then((response) => {
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      return response.text();
    })
    .then((data) => {
      result = data ? JSON.parse(data) : {};
    })
    .catch((error) => {
      console.error("Error:", error);
    });

  return result;
};

export const getCutAndFill = async ({
  layerId,
  objectId,
  geometry,
}: GetCutAndFillOptions): Promise<CutAndFill | undefined> => {
  const baseUrl = `${BASE_URL}/getCutAndFill`;
  let url = "";

  if (geometry) {
    const geo = JSON.stringify(geometry);
    url = `${baseUrl}?geometry=${geo}`;
  } else if (layerId !== undefined && objectId !== undefined) {
    url = `${baseUrl}?layerId=${layerId}&featureId=${objectId}`;
  } else {
    return undefined;
  }

  try {
    const accessToken = localStorage.getItem("accessToken");
    const headers: HeadersInit = {};
    if (accessToken) headers.Authorization = accessToken;

    const response = await fetch(url, { headers });
    if (!response.ok) throw new Error("Network response was not ok");
    const text = await response.text();
    return text ? JSON.parse(text) : ({} as CutAndFill);
  } catch (error) {
    return undefined;
  }
};

export const getAttachmentDownload = async (id: number, filename: string) => {
  try {
    const response = await fetch(`${BASE_URL}/GetAttachment?id=${id}`);
    if (!response.ok) throw new Error("Failed to download the file");
    return await response.blob();
  } catch (error) {
    console.error("Error while downloading the file:", error);
    throw error;
  }
};

export const getAttachmentImageUrl = async (
  id: number,
  inModal?: boolean,
): Promise<string | { url: string; id: number }> => {
  try {
    const response = await fetch(`${BASE_URL}/GetAttachment?id=${id}`);

    if (!response.ok) {
      throw new Error(`Failed to load the image. Status: ${response.status}`);
    }

    const contentType = response.headers.get("content-type");
    if (!contentType || !contentType.startsWith("image/")) {
      throw new Error("The fetched file is not an image.");
    }

    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    if (inModal) return { url: url, id: id };

    return url;
  } catch (error) {
    console.error("Error while fetching the image URL:", error);
    throw error;
  }
};

export const getKrookieFormationTypes = async () => {
  const responce = await fetch(KROOKIE_URL + "/GetKrookieFormationTypes").then(
    (result) => result.json(),
  );

  return responce;
};

export const saveAttachments = async (
  attachments: File[],
  type: "image" | "document" | "floorPlan",
  layerId: string,
  featureId: string,
) => {
  const url = BASE_URL + "/SaveAttachments";
  const formData = new FormData();

  formData.append(
    "KindId",
    type === "image" ? "1" : type === "document" ? "2" : "1",
  );
  formData.append(
    "TypeId",
    type === "image" ? "3" : type === "document" ? "4" : "2",
  );
  formData.append("LayerId", layerId);
  formData.append("FeatureId", featureId);

  attachments.forEach((file) => {
    formData.append("data", file, file.name);
  });

  await fetchWithRefresh(url, {
    method: "POST",
    headers: {
      Authorization: localStorage.getItem("accessToken")!,
    },
    body: formData,
  });
};

export const deleteAttachments = async (ids: number[]) => {
  const url = BASE_URL + "/DeleteAttachments";

  await fetchWithRefresh(url, {
    method: "POST",
    headers: {
      Authorization: localStorage.getItem("accessToken")!,
      "Content-Type": "application/json",
    },
    body: JSON.stringify(ids),
  });
};

export const getPreviousKrookieNum = async (objectIds: number | number[]) => {
  const idsArray = Array.isArray(objectIds) ? objectIds : [objectIds];

  const params = new URLSearchParams();
  idsArray.forEach((id) => params.append("objectId", id.toString()));

  const url = `${KROOKIE_URL}/GetPreviousKrookieNum?${params.toString()}`;
  try {
    const response = await fetchWithRefresh(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: localStorage.getItem("accessToken")!,
      },
    });
    return response;
  } catch (error) {
    console.error("Error:", error);
    return null;
  }
};

export const saveKrookie = async (formData: FormData) => {
  const url = KROOKIE_URL + "/SaveKrookies";
  try {
    const data = await fetchWithRefresh(url, {
      method: "POST",
      headers: {
        Authorization: localStorage.getItem("accessToken")!,
      },
      body: formData,
    });
    return data;
  } catch (error) {
    throw error;
  }
};

export const getKrookies = async () => {
  const url = KROOKIE_URL + `/GetKrookies`;

  const accessToken = localStorage.getItem("accessToken");

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error("Network response was not ok");
    }

    return await response.json();
  } catch (error) {
    console.error("Error:", error);
    return null;
  }
};

export const getKrookieStatuses = async () => {
  const url = KROOKIE_URL + `/GetKrookieStatuses`;

  const accessToken = localStorage.getItem("accessToken");

  try {
    const response = await fetch(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `${accessToken}`,
      },
    });

    if (!response.ok) {
      throw new Error("Network response was not ok");
    }

    return await response.json();
  } catch (error) {
    console.error("Error:", error);
    return null;
  }
};

export const getDictionaries = async (
  layer: Layer,
  tokens?: ArcgisTokenMap,
) => {
  const fields = await GetFields(layer, tokens);

  const getCodedValues = (fieldName: string) =>
    (fields?.find((f) => f.name === fieldName)?.domain as any)?.codedValues ??
    [];

  return {
    cities: getCodedValues("estateEn"),
    landUse: getCodedValues("Landuse"),
    phase: getCodedValues("parent_location_Serial"),
    estateAr: getCodedValues("estateAr"),
    status: getCodedValues("status"),
    locationStatusCode: getCodedValues("location_status_code"),
  };
};

export const getKrookiesInfo = async (
  id: number,
): Promise<KrookieDataResponse> => {
  const url = KROOKIE_URL + `/GetKrookiesInfo?sessionId=${id}`;

  try {
    const response = await fetchWithRefresh(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: localStorage.getItem("accessToken")!,
      },
    });

    return response;
  } catch (error) {
    throw error;
  }
};

export const GetKrookieDoc = async (id: number) => {
  const url = KROOKIE_URL + `/GetKrookieDoc?krookieId=${id}`;
  try {
    const result = await fetchWithRefresh(url, {
      method: "GET",
      headers: {
        Accept: "application/pdf",
        Authorization: localStorage.getItem("accessToken")!,
      },
    });
    if (result instanceof Blob) {
      const tempUrl = window.URL.createObjectURL(result);
      window.open(tempUrl);
      return result;
    } else {
      throw new Error("Ошибка запроса");
    }
  } catch (error) {
    throw error;
  }
};

export const saveHistoryComment = async (
  historyLogId: number,
  comment: string,
) => {
  const url =
    KROOKIE_URL +
    `/SaveHistoryComment?comment=${encodeURIComponent(comment)}&historyLogId=${historyLogId}`;
  try {
    const response = await fetchWithRefresh(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: localStorage.getItem("accessToken")!,
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const changeKrookieStatus = async (
  statusId: number,
  krookies: Array<{
    krookieId: string;
    changeNum: string;
    documentBody?: File;
    newPlotId?: string;
    [key: string]: any;
  }>,
) => {
  const url = `${KROOKIE_URL}/ChangeKrookieStatuses`;

  const formData = new FormData();
  formData.append("StatusId", statusId.toString());

  krookies.forEach((krookie, index) => {
    Object.keys(krookie).forEach((key) => {
      formData.append(`Krookies[${index}].${key}`, krookie[key]);
    });
  });

  try {
    const response = await fetchWithRefresh(url, {
      method: "POST",
      body: formData,
      headers: {
        Authorization: localStorage.getItem("accessToken") || "",
      },
    });
    return response;
  } catch (error) {
    throw error;
  }
};

export const getKrookieDoc = async (ids: number[]) => {
  const accessToken = localStorage.getItem("accessToken");
  const documents = [];

  const processId = async (id: number) => {
    await new Promise((resolve) => setTimeout(resolve, 50));
    console.log(id);
    const url = `${KROOKIE_URL}/GetKrookieDoc?krookieId=${id}`;

    try {
      const response = await fetch(url, {
        method: "GET",
        headers: {
          Authorization: `${accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(
          `Ошибка при получении документа с ID ${id}: ${response.statusText}`,
        );
      }

      const documentBlob = await response.blob();
      documents.push(documentBlob);

      const documentUrl = URL.createObjectURL(documentBlob);
      window.open(documentUrl, "_blank");
    } catch (error) {
      console.error(error);
    }
  };

  for (const id of ids) {
    await processId(id);
  }
};

export const GetKrookieCoordinates = async (
  krookieId: number,
): Promise<ResponseCoordinates> => {
  const url = KROOKIE_URL + `/GetKrookieCoordinates?krookieId=${krookieId}`;

  try {
    const response = await fetchWithRefresh(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: localStorage.getItem("accessToken")!,
      },
    });

    return response;
  } catch (error) {
    throw error;
  }
};

export const saveReport = async (formData: FormData): Promise<number> => {
  const url = REPORTS_URL + "/SaveReport";
  try {
    const data = await fetchWithRefresh(url, {
      method: "POST",
      headers: {
        Authorization: localStorage.getItem("accessToken")!,
      },
      body: formData,
    });
    return data;
  } catch (error) {
    throw error;
  }
};

export const getReportDocument = async (
  reportId: number,
  type: "word" | "pdf",
) => {
  const url =
    REPORTS_URL + `/GetReportDocument?reportId=${reportId}&type=${type}`;

  try {
    const response = await fetchWithRefresh(
      url,
      {
        method: "GET",
        headers: {
          Authorization: localStorage.getItem("accessToken")!,
        },
      },
      true,
    );

    const disposition = response.headers.get("Content-Disposition");
    let filename = disposition?.split("filename=")[1];

    if (filename) {
      filename = filename.replace(/['"]/g, "");
    } else {
      const ext = type === "pdf" ? "pdf" : "docx";
      filename = `Report.${ext}`;
    }

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    throw error;
  }
};

export const getReports = async (): Promise<ResponseReports[]> => {
  const url = REPORTS_URL + `/GetReports`;

  try {
    const response = await fetchWithRefresh(url, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: localStorage.getItem("accessToken")!,
      },
    });

    return response;
  } catch (error) {
    throw error;
  }
};

export const getReportHistory = async (
  reportId: number,
): Promise<ReportHistoryResponse> => {
  const url = REPORTS_URL + `/GetReportHistory?reportId=${reportId}`;

  try {
    const response = await fetchWithRefresh(url, {
      method: "GET",
      headers: {
        Authorization: localStorage.getItem("accessToken")!,
      },
    });

    return response;
  } catch (error) {
    throw error;
  }
};

export const deleteReport = async (reportId: number) => {
  const url = REPORTS_URL + `/DeleteReport?id=${reportId}`;
  try {
    const response = await fetchWithRefresh(url, {
      method: "POST",
      headers: {
        Authorization: localStorage.getItem("accessToken")!,
      },
    });

    return response;
  } catch (error) {
    throw error;
  }
};

export const changeReportStatus = async (reportId: number, status: string) => {
  const url = REPORTS_URL + `/ChangeReportStatus`;

  try {
    const response = await fetchWithRefresh(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: localStorage.getItem("accessToken")!,
      },
      body: JSON.stringify({ reportId, status }),
    });

    return response;
  } catch (error) {
    throw error;
  }
};

export const getProjects = async (): Promise<ProjectsResponse[]> => {
  const url = UTILITIES_URL + `/GetProjects`;

  try {
    const response = await fetchWithRefresh(url, {
      method: "GET",
      headers: {
        Authorization: localStorage.getItem("accessToken")!,
      },
    });

    return response;
  } catch (error) {
    throw error;
  }
};

export const getContractorProjects = async (
  regNumber: string,
): Promise<ProjectsResponse[]> => {
  const url = UTILITIES_URL + `/GetContractorProjects?regNumber=${regNumber}`;

  try {
    const response = await fetchWithRefresh(url, {
      method: "GET",
      headers: {
        Authorization: localStorage.getItem("accessToken")!,
      },
    });

    return response;
  } catch (error) {
    throw error;
  }
};

export const viewNotification = async (notificationId: number) => {
  const url =
    UTILITIES_URL + `/ViewNotification?notificationId=${notificationId}`;

  try {
    const response = await fetchWithRefresh(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: localStorage.getItem("accessToken")!,
      },
    });

    return response;
  } catch (error) {
    throw error;
  }
};

export const convertGeometryToArcgis = async (wkid: number, files: File[]) => {
  const url = CONVERTER_URL + "/Convert";
  const formData = new FormData();

  formData.append("WkId", wkid.toString());

  files.forEach((file) => {
    formData.append("Files", file);
  });
  try {
    const response = await fetch(url, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`${response.status}`);
    }

    return response.json();
  } catch (error) {
    throw new Error(`${error}`);
  }
};

export const saveStageFiles = async (payload: any) => {
  const url = UTILITIES_URL + `/SaveStageFiles`;

  try {
    const response = await fetchWithRefresh(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: localStorage.getItem("accessToken")!,
      },
      body: JSON.stringify(payload),
    });

    return response;
  } catch (error) {
    throw error;
  }
};

export const getProjectFiles = async (
  projectId: number,
): Promise<getProjectsResponse> => {
  const url = UTILITIES_URL + `/GetProjectFiles?projectId=${projectId}`;

  try {
    const response = await fetchWithRefresh(url, {
      method: "GET",
      headers: {
        Authorization: localStorage.getItem("accessToken")!,
      },
    });

    return response;
  } catch (error) {
    throw error;
  }
};

export const changeStageStatus = async (payload: any) => {
  const url = UTILITIES_URL + `/ChangeStageStatus`;

  try {
    const response = await fetchWithRefresh(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: localStorage.getItem("accessToken")!,
      },
      body: JSON.stringify(payload),
    });

    return response;
  } catch (error) {
    throw error;
  }
};

export const getProjectLogs = async (
  projectId: number,
): Promise<GetProjectLogsResponse> => {
  const url = UTILITIES_URL + `/GetProjectLogs?projectId=${projectId}`;

  try {
    const response = await fetchWithRefresh(url, {
      method: "GET",
      headers: {
        Authorization: localStorage.getItem("accessToken")!,
      },
    });

    return response;
  } catch (error) {
    throw error;
  }
};

export const GetPreviewKrookie = async (id: number) => {
  const url = KROOKIE_URL + `/GetPreviewKrookie?krookieId=${id}`;

  try {
    const response = await fetchWithRefresh(
      url,
      {
        method: "GET",
        headers: {
          Authorization: localStorage.getItem("accessToken")!,
          Accept: "image/png",
        },
      },
      true,
    );

    return response.blob();
  } catch (error) {
    throw error;
  }
};

export const changeAttachmentsLayer = async (
  layerId: string,
  featureId: string,
) => {
  const url = BASE_URL + `/ChangeAttachmentsLayer`;

  try {
    const response = await fetchWithRefresh(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: localStorage.getItem("accessToken")!,
      },
      body: JSON.stringify({ layerId, featureId }),
    });

    return response;
  } catch (error) {
    throw error;
  }
};

export const getStructureFiles = async () => {
  const url = UTILITIES_URL + `/StructureFiles`;

  try {
    const response = await fetchWithRefresh(
      url,
      {
        method: "GET",
        headers: {
          Authorization: localStorage.getItem("accessToken")!,
        },
      },
      true,
    );

    if (!response.ok) {
      throw new Error(`Error status: ${response.status}`);
    }

    const disposition = response.headers.get("Content-Disposition");

    let filename = disposition?.split("filename=")[1].replace(/['"]/g, "");

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    throw error;
  }
};

export const getStageFiles = async (stageId: number) => {
  const url = UTILITIES_URL + `/GetStageFiles?stageId=${stageId}`;

  try {
    const response = await fetchWithRefresh(
      url,
      {
        method: "GET",
        headers: {
          Authorization: localStorage.getItem("accessToken")!,
        },
      },
      true,
    );

    if (!response.ok) {
      throw new Error(`Error status: ${response.status}`);
    }

    const disposition = response.headers.get("Content-Disposition");

    let filename = disposition?.split("filename=")[1].replace(/['"]/g, "");

    const blob = await response.blob();
    const blobUrl = window.URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    window.URL.revokeObjectURL(blobUrl);
  } catch (error) {
    throw error;
  }
};
